// web/src/scene/loaders/DXFLoader.tsx
import { useEffect, useRef } from 'react'
import type { MutableRefObject } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import DxfParser from 'dxf-parser'

import { useAppSelector } from '../../app/useAppSelector'
import { useAppDispatch } from '../../app/useAppDispatch'
import { mapActions } from '@shared/slices/mapSlice'
import { useSceneRef } from '../SceneRefContext'
import { convertDXFToThreeJS } from './dxfToThree'
import { applyLevelFilter } from './levelFilterUtils'

interface DXFLoaderProps {
  onBoundsReady?: (box: THREE.Box3) => void
}

export function DXFLoader({ onBoundsReady }: DXFLoaderProps) {
  const { scene } = useThree()
  const defaultMapFile = useAppSelector(s => s.map.defaultMapFile)
  const wireframe = useAppSelector(s => s.viewer.showWireframe)
  const filterLevel = useAppSelector(s => s.rtls.filterLevel)
  const dispatch = useAppDispatch()
  const sceneRefHolder = useSceneRef()

  const modelRef = useRef<THREE.Group | null>(null)

  // Stable onBoundsReady ref — keeps the load effect's deps minimal.
  const onBoundsReadyRef = useRef(onBoundsReady)
  useEffect(() => {
    onBoundsReadyRef.current = onBoundsReady
  }, [onBoundsReady])

  // --- Load effect -------------------------------------------------------
  useEffect(() => {
    if (!defaultMapFile) return
    const ext = defaultMapFile.split('.').pop()?.toLowerCase()
    if (ext !== 'dxf') return

    let cancelled = false
    const url = `/samples/${defaultMapFile}`

    // Dispose any previously-mounted model.
    disposeCurrentModel(scene, modelRef)

    dispatch(mapActions.setLoading({
      fileName: defaultMapFile,
      fileType: 'dxf',
      fileSizeBytes: 0,
    }))

    ;(async () => {
      try {
        const res = await fetch(url)
        if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${url}`)
        const content = await res.text()
        if (cancelled) return

        // parseSync is blocking — acceptable for sample files.
        const parser = new DxfParser()
        const parsed = parser.parseSync(content)

        if (!parsed) {
          throw new Error('DXF parser returned null — file may be malformed')
        }

        if (cancelled) return

        const model = convertDXFToThreeJS(parsed)
        scene.add(model)
        modelRef.current = model

        // Apply level filter immediately after mount (M1 passes null = show all).
        applyLevelFilter(model, null)

        // Compute bounding box
        const box = new THREE.Box3().setFromObject(model)
        const center = new THREE.Vector3()
        box.getCenter(center)

        // Count vertices — DXF scenes contain Lines, Meshes, and Points.
        let vertexCount = 0
        model.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Line || child instanceof THREE.Mesh) {
            const geo = (child as any).geometry
            if (geo?.attributes?.position) {
              vertexCount += geo.attributes.position.count
            }
          }
        })

        dispatch(mapActions.setModelLoaded({
          boundingBox: {
            min: { x: box.min.x, y: box.min.y, z: box.min.z },
            max: { x: box.max.x, y: box.max.y, z: box.max.z },
          },
          vertexCount,
          center: { x: center.x, y: center.y, z: center.z },
        }))

        onBoundsReadyRef.current?.(box)
        sceneRefHolder.current?.camera.fitToModel()
      } catch (err: unknown) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String(err)
        console.error('[DXFLoader]', msg)
        dispatch(mapActions.setLoadError(`DXF load failed: ${msg}`))
      }
    })()

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMapFile, dispatch, scene, sceneRefHolder])

  // --- Wireframe reactive effect -----------------------------------------
  // Note: THREE.LineBasicMaterial does not support wireframe. The 'wireframe' in m
  // guard safely skips line materials and only toggles on MeshStandardMaterial etc.
  useEffect(() => {
    if (!modelRef.current) return
    modelRef.current.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        mats.forEach((m) => {
          if (m && 'wireframe' in m) (m as any).wireframe = wireframe
        })
      }
    })
  }, [wireframe])

  // --- Level filter reactive effect --------------------------------------
  useEffect(() => {
    // M1 has no zRange computation — pass null (no clipping).
    applyLevelFilter(modelRef.current, null)
  }, [filterLevel])

  return null
}

// --- Helpers -------------------------------------------------------------
function disposeCurrentModel(
  scene: THREE.Scene,
  modelRef: MutableRefObject<THREE.Group | null>,
) {
  const current = modelRef.current
  if (!current) return
  current.traverse((obj) => {
    if (obj instanceof THREE.Mesh || obj instanceof THREE.Line) {
      obj.geometry?.dispose()
      const material = (obj as any).material
      if (Array.isArray(material)) {
        material.forEach((m: THREE.Material) => m.dispose())
      } else if (material) {
        material.dispose()
      }
    }
    if (obj instanceof THREE.Points) {
      obj.geometry?.dispose()
      ;(obj.material as THREE.PointsMaterial)?.dispose()
    }
  })
  scene.remove(current)
  modelRef.current = null
}
